// Auswertung der ticketbezogenen Zeiten: nach Objekt, Mitarbeiter und Ticket.
//
// Bewusste Trennung: die Summen hier stammen aus `ticket_zeiten` (wofür wurde
// gearbeitet), nicht aus der Stempeluhr. Die Kennzahl „Abdeckung" stellt beide
// nebeneinander, damit auffällt, wenn viel Arbeitszeit ohne Ticketbezug bleibt.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Buildings, Car, ChartPieSlice, Receipt, Ticket, UserCircle, Users, Warning, type Icon } from '@phosphor-icons/react';
import { supabase } from '../../../lib/supabase/client';
import { ladeFahrten, ladeStempel, ladeTicketZeitenZeitraum, type Fahrt } from '../../../lib/zeiterfassung/api';
import { abdeckung, aufteilung, gruppiere, type TicketZeit } from '../../../lib/zeiterfassung/ticketzeit';
import { nettoMinuten, stundenText, type Stempel } from '../../../lib/zeiterfassung/berechnung';

interface Props {
  vonTag: string;
  bisTag: string;
  mitarbeiterId: string;
  /** Ohne Einschränkung auswerten (Personal/Geschäftsführung) */
  alleMitarbeiter: boolean;
  namen: Record<string, string>;
}

const TicketzeitAuswertung: React.FC<Props> = ({ vonTag, bisTag, mitarbeiterId, alleMitarbeiter, namen }) => {
  const [zeiten, setZeiten] = useState<TicketZeit[]>([]);
  const [stempel, setStempel] = useState<Stempel[]>([]);
  const [objekte, setObjekte] = useState<Record<string, string>>({});
  const [tickets, setTickets] = useState<Record<string, string>>({});
  const [fahrten, setFahrten] = useState<Fahrt[]>([]);
  // Objekt → Eigentümer/WEG. Ein Objekt kann mehreren gehören; die Zeit wird dann
  // JEDEM zugeordnet (nicht geteilt) — sonst stimmt keine der beiden Summen.
  const [eigentuemerJeObjekt, setEigentuemerJeObjekt] = useState<Record<string, string[]>>({});
  const [eigentuemerNamen, setEigentuemerNamen] = useState<Record<string, string>>({});
  const [laedt, setLaedt] = useState(true);

  const laden = useCallback(async () => {
    setLaedt(true);
    const [tz, st, fa] = await Promise.all([
      ladeTicketZeitenZeitraum(vonTag, bisTag, alleMitarbeiter ? null : mitarbeiterId),
      mitarbeiterId ? ladeStempel(mitarbeiterId, vonTag, bisTag) : Promise.resolve([]),
      ladeFahrten({ vonTag, bisTag, mitarbeiterId: alleMitarbeiter ? null : mitarbeiterId }),
    ]);
    setZeiten(tz);
    setStempel(st);
    setFahrten(fa);

    const objektIds = [...new Set([...tz.map((z) => z.objekt_id), ...fa.map((f) => f.objekt_id)].filter(Boolean))] as string[];
    if (objektIds.length) {
      const { data } = await supabase.from('objekte').select('id, objektnummer, bezeichnung, strasse, hausnummer').in('id', objektIds);
      setObjekte(Object.fromEntries((data || []).map((o: any) => [
        o.id,
        [o.objektnummer, o.bezeichnung || `${o.strasse || ''} ${o.hausnummer || ''}`.trim()].filter(Boolean).join(' '),
      ])));
    }
    if (objektIds.length) {
      const { data: verkn } = await supabase
        .from('objekt_eigentuemer')
        .select('objekt_id, eigentuemer_id, eigentuemer:eigentuemer(vorname, nachname, firma_name)')
        .in('objekt_id', objektIds);
      const zuordnung: Record<string, string[]> = {};
      const namen: Record<string, string> = {};
      for (const v of (verkn || []) as any[]) {
        (zuordnung[v.objekt_id] ||= []).push(v.eigentuemer_id);
        const e = Array.isArray(v.eigentuemer) ? v.eigentuemer[0] : v.eigentuemer;
        namen[v.eigentuemer_id] = (e?.firma_name || '').trim()
          || [e?.vorname, e?.nachname].filter(Boolean).join(' ').trim()
          || 'Unbekannt';
      }
      setEigentuemerJeObjekt(zuordnung);
      setEigentuemerNamen(namen);
    }
    const ticketIds = [...new Set(tz.map((z) => z.ticket_id))];
    if (ticketIds.length) {
      const { data } = await supabase.from('tickets').select('id, titel, ticket_nummer').in('id', ticketIds);
      setTickets(Object.fromEntries((data || []).map((t: any) => [t.id, t.titel || `Ticket ${t.ticket_nummer || ''}`])));
    }
    setLaedt(false);
  }, [vonTag, bisTag, mitarbeiterId, alleMitarbeiter]);

  useEffect(() => { void laden(); }, [laden]);

  const summe = useMemo(() => aufteilung(zeiten), [zeiten]);
  const arbeitsMinuten = useMemo(() => stempel.reduce((s, x) => s + nettoMinuten(x), 0), [stempel]);
  const anteil = abdeckung(alleMitarbeiter ? 0 : summe.gesamt, alleMitarbeiter ? 0 : arbeitsMinuten);

  const nachObjekt = useMemo(() => gruppiere(zeiten, (z) => z.objekt_id), [zeiten]);
  const nachTicket = useMemo(() => gruppiere(zeiten, (z) => z.ticket_id), [zeiten]);
  const nachMitarbeiter = useMemo(() => gruppiere(zeiten, (z) => z.mitarbeiter_id), [zeiten]);
  // Nach Eigentümer/WEG: die Zeit eines Objekts zählt bei jedem hinterlegten Eigentümer.
  const nachEigentuemer = useMemo(() => {
    const map = new Map<string, { minuten: number; abrechenbar: number; anzahl: number }>();
    for (const z of zeiten) {
      const ids = z.objekt_id ? eigentuemerJeObjekt[z.objekt_id] || [] : [];
      for (const id of ids) {
        const e = map.get(id) || { minuten: 0, abrechenbar: 0, anzahl: 0 };
        const m = z.ende ? Math.max(0, Math.round((new Date(z.ende).getTime() - new Date(z.beginn).getTime()) / 60000)) : 0;
        e.minuten += m;
        if (z.abrechenbar) e.abrechenbar += m;
        e.anzahl += 1;
        map.set(id, e);
      }
    }
    return [...map.entries()].map(([key, v]) => ({ key, ...v })).sort((a, b) => b.minuten - a.minuten);
  }, [zeiten, eigentuemerJeObjekt]);

  const kmGesamt = useMemo(() => fahrten.reduce((s, f) => s + Number(f.kilometer), 0), [fahrten]);
  // Kilometer je Objekt — dieselbe Sortierung wie die Zeitlisten (größte zuerst).
  const kmJeObjekt = useMemo(() => {
    const map = new Map<string, number>();
    for (const f of fahrten) {
      if (!f.objekt_id) continue;
      map.set(f.objekt_id, (map.get(f.objekt_id) || 0) + Number(f.kilometer));
    }
    return [...map.entries()].map(([key, km]) => ({ key, km })).sort((a, b) => b.km - a.km);
  }, [fahrten]);

  if (laedt) {
    return (
      <div className="flex justify-center" style={{ padding: '60px 0' }}>
        <span className="animate-spin" style={{ width: 22, height: 22, borderRadius: '50%', border: '2.5px solid var(--fill3)', borderTopColor: 'var(--acc)' }} />
      </div>
    );
  }

  const ueberzogen = anteil !== null && anteil > 100;
  const abrechenbarJeObjekt = nachObjekt.filter((o) => o.abrechenbar > 0);

  return (
    <div className="flex flex-col" style={{ gap: 12 }}>
      <div className="grid" style={{ gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))' }}>
        <Kachel icon={Ticket} farbe="var(--acc)" flaeche="var(--acc-soft)" label="Auf Tickets gebucht" wert={stundenText(summe.gesamt)} tip="Auf Tickets gebucht – Summe aller Ticketzeiten im Monat" />
        <Kachel icon={Receipt} farbe="var(--ok)" flaeche="var(--ok-soft)" label="Abrechenbar" wert={stundenText(summe.abrechenbar)} wertFarbe="var(--ok)" tip="Abrechenbar – Als abrechenbar gekennzeichnete Zeit" />
        <Kachel icon={Buildings} farbe="var(--info)" flaeche="var(--info-soft)" label="Intern" wert={stundenText(summe.intern)} tip="Intern – Nicht abrechenbare Ticketzeit" />
        <Kachel icon={Car} farbe="var(--pur)" flaeche="var(--pur-soft)" label="Gefahren" wert={`${kmGesamt.toLocaleString('de-DE')} km`} tip="Gefahren – Erfasste Fahrten im Monat" />
        {!alleMitarbeiter && (
          <Kachel icon={ChartPieSlice} farbe="var(--warn)" flaeche="var(--warn-soft)" label="Anteil an der Arbeitszeit" wert={anteil === null ? '—' : `${anteil} %`}
            wertFarbe={ueberzogen ? 'var(--warn)' : undefined} tip="Anteil – Ticketzeit im Verhältnis zur gestempelten Arbeitszeit" />
        )}
      </div>

      {ueberzogen && (
        <div role="alert" className="flex items-center" style={{ gap: 10, padding: '12px 16px', borderRadius: 18, background: 'var(--warn-soft)', color: 'var(--warn)', fontSize: 13, fontWeight: 700 }}>
          <Warning size={18} weight="fill" className="shrink-0" />Auf Tickets steht mehr Zeit als gestempelt wurde — vermutlich doppelt gebucht.
        </div>
      )}

      <div className="grid items-start" style={{ gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))' }}>
        <Liste titel="Nach Objekt" icon={Buildings} zeilen={nachObjekt} beschriftung={(k) => objekte[k] || 'Unbekanntes Objekt'} />
        <Liste titel="Nach Eigentümer / WEG" icon={UserCircle} zeilen={nachEigentuemer} beschriftung={(k) => eigentuemerNamen[k] || 'Unbekannt'} />
        <Liste titel="Nach Ticket" icon={Ticket} zeilen={nachTicket} beschriftung={(k) => tickets[k] || 'Ticket'} />
        {alleMitarbeiter && (
          <Liste titel="Nach Mitarbeiter" icon={Users} zeilen={nachMitarbeiter} beschriftung={(k) => namen[k] || 'Unbekannt'} />
        )}
        {kmJeObjekt.length > 0 && (
          <Widget titel="Kilometer je Objekt" icon={Car} farbe="var(--info)" flaeche="var(--info-soft)">
            {kmJeObjekt.map((o) => (
              <div key={o.key} className="flex items-center justify-between" style={{ gap: 10, fontSize: 13 }}>
                <span className="min-w-0 flex-1 truncate" style={{ fontWeight: 700 }}>{objekte[o.key] || 'Unbekanntes Objekt'}</span>
                <b className="shrink-0 tabular-nums" style={{ fontWeight: 800 }}>{o.km.toLocaleString('de-DE')} km</b>
              </div>
            ))}
          </Widget>
        )}
        <Widget titel="Abrechenbar je Objekt" icon={Receipt} farbe="var(--ok)" flaeche="var(--ok-soft)">
          {abrechenbarJeObjekt.length === 0 ? (
            <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Nichts als abrechenbar gekennzeichnet</span>
          ) : abrechenbarJeObjekt.map((o) => (
            <div key={o.key} className="flex items-center justify-between" style={{ gap: 10, fontSize: 13 }}>
              <span className="min-w-0 flex-1 truncate" style={{ fontWeight: 700 }}>{objekte[o.key] || 'Unbekanntes Objekt'}</span>
              <b className="shrink-0 tabular-nums" style={{ fontWeight: 800, color: 'var(--ok)' }}>{stundenText(o.abrechenbar)}</b>
            </div>
          ))}
        </Widget>
      </div>
    </div>
  );
};

/** Kennzahl-Kachel: Symbol links, Wert und Beschriftung rechts (Screen 03). */
function Kachel({ icon: I, farbe, flaeche, label, wert, wertFarbe, tip }: { icon: Icon; farbe: string; flaeche: string; label: string; wert: string; wertFarbe?: string; tip: string }) {
  return (
    <div data-tip={tip} className="immo-glas flex items-center" style={{ gap: 12, padding: '14px 16px', borderRadius: 24 }}>
      <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 42, height: 42, borderRadius: 14, background: flaeche, color: farbe }}><I size={20} weight="fill" /></span>
      <div className="flex min-w-0 flex-col">
        <span className="truncate tabular-nums" style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', color: wertFarbe ?? 'var(--ink)' }}>{wert}</span>
        <span className="truncate" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink3)' }}>{label}</span>
      </div>
    </div>
  );
}

function Widget({ titel, icon: I, farbe = 'var(--acc)', flaeche = 'var(--acc-soft)', children }: { titel: string; icon: Icon; farbe?: string; flaeche?: string; children: React.ReactNode }) {
  return (
    <section className="immo-glas flex flex-col" style={{ gap: 12, padding: 18, borderRadius: 26 }}>
      <h2 className="m-0 flex items-center" style={{ gap: 8, fontSize: 15, fontWeight: 800 }}>
        <span aria-hidden className="inline-flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: 10, background: flaeche, color: farbe }}><I size={15} weight="fill" /></span>
        {titel}
      </h2>
      {children}
    </section>
  );
}

/** Liste mit Balken relativ zum größten Eintrag und grünem Abrechenbar-Chip — Top 12 wie bisher. */
function Liste({ titel, icon, zeilen, beschriftung }: {
  titel: string; icon: Icon; zeilen: { key: string; minuten: number; abrechenbar: number; anzahl: number }[]; beschriftung: (key: string) => string;
}) {
  return (
    <Widget titel={titel} icon={icon}>
      {zeilen.length === 0 ? (
        <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Keine Zeiten im Zeitraum</span>
      ) : zeilen.slice(0, 12).map((z) => {
        const anteilBalken = zeilen[0].minuten > 0 ? (z.minuten / zeilen[0].minuten) * 100 : 0;
        return (
          <div key={z.key} className="flex flex-col" style={{ gap: 5 }}>
            <div className="flex items-center" style={{ gap: 8, fontSize: 13 }}>
              <span className="min-w-0 flex-1 truncate" style={{ fontWeight: 700 }}>{beschriftung(z.key)}</span>
              {z.abrechenbar > 0 && (
                <span data-tip="Abrechenbar – Davon gegenüber dem Eigentümer abrechenbar" className="inline-flex shrink-0 items-center"
                  style={{ height: 20, padding: '0 8px', borderRadius: 10, fontSize: 10.5, fontWeight: 800, background: 'var(--ok-soft)', color: 'var(--ok)' }}>{stundenText(z.abrechenbar)}</span>
              )}
              <b className="shrink-0 tabular-nums" style={{ fontWeight: 800 }}>{stundenText(z.minuten)}</b>
            </div>
            <span aria-hidden className="block overflow-hidden" style={{ height: 6, borderRadius: 3, background: 'var(--fill2)' }}>
              <span className="block h-full" style={{ width: `${anteilBalken}%`, borderRadius: 3, background: 'var(--acc-fill)' }} />
            </span>
          </div>
        );
      })}
    </Widget>
  );
}

export default TicketzeitAuswertung;
